import Layout from '@src/components/Layout';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityLogsQuery } from '~/QueryHooks/ActivityLogsQuery';
import { useDebounce } from 'use-lodash-debounce'
import { El } from '~/components/Layout/style';
import { Dropdown, Input } from 'semantic-ui-react';
import i18n from '~/components/i18n';
import TableComponent from '~/components/TableComponent';
import { DateInput } from 'semantic-ui-calendar-react';
import { useAuth } from '~/routes/components/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { RouteTypes } from '~/routes/constants';
import { FARMER_MANAGER_ROLE_SLUG, LIGHT_USER_ROLE_SLUG, OPERATIVE_ROLE_SLUG } from '~/Constants';
import { FiltersWrapper } from './style';
import ExcelExport from '~/components/ExcelExport';

const getTableColumnHeaders = () => [
  {
    name: i18n.t('Date'),
    key: 'createdAt',
    type: 'date',
    format: 'DD.MM.YYYY HH:mm'
  },
  {
    name: i18n.t('User'),
    key: 'name'
  },
  {
    name: i18n.t('Supplier No.'),
    key: 'supplierNo'
  },
  {
    name: i18n.t('Role'),
    key: 'roleLabel'
  },
  {
    name: i18n.t('Action'),
    key: 'label'
  },
  {
    name: i18n.t('Browser'),
    key: 'browser',
    type: 'browser'
  },
  {
    name: i18n.t('Device'),
    key: 'device',
    type: 'device'
  }
]

const ActivityLogs: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 30,
    search: '',
    type: '',
    action: '',
    dateFrom: '',
    dateTo: ''
  })
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const debouncedValue = useDebounce(search, 300)
  const { systemData: { languages, logged: { languageId, roleSlug }, operativeUsersCanSeeFarmersInfo } } = useAuth()
  const languageLocal = languages.find(language => language.id === languageId)
  const lightUserLogged = (roleSlug === LIGHT_USER_ROLE_SLUG) || (roleSlug === FARMER_MANAGER_ROLE_SLUG)
  const operativeUserLogged = roleSlug === OPERATIVE_ROLE_SLUG

  const {
    isLoading: isBusyData,
    data: activityLogData,
    isFetching: isFetching,
    refetch: fetchFilteredData
  } = ActivityLogsQuery.getActivityLogs({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1,
    dateFrom: dataFiltered.dateFrom !== '' ? parseDate(dataFiltered.dateFrom) : '',
    dateTo: dataFiltered.dateTo !== '' ? parseDate(dataFiltered.dateTo) : ''
  })

  function parseDate(value) {
    const arrayOfValues = value.split('.')
    return `${arrayOfValues[2]}-${arrayOfValues[1]}-${arrayOfValues[0]}`
  }

  const {
    data: activityLogOptions,
    refetch: fetchOptions,
    isLoading: isBusyActivityLogOptions
  } = ActivityLogsQuery.getActivityLogsOptions()

  useEffect(() => {
    fetchOptions()
  }, [t])

  useEffect(() => {
    if (dataFiltered) {
      fetchFilteredData()
    }
  }, [t, dataFiltered])

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedValue, page: 0 })
  }, [debouncedValue])

  const handleKeyPress = (event) => {
    event.preventDefault();
  }

  function renderActions() {
    return <>
      <ExcelExport
        url={'activity-logs/export'}
        fileName={t('activity logs')}
        data={{
          ...dataFiltered,
          page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1,
          dateFrom: dataFiltered.dateFrom !== '' ? parseDate(dataFiltered.dateFrom) : '',
          dateTo: dataFiltered.dateTo !== '' ? parseDate(dataFiltered.dateTo) : ''
        }}
      />
      {(operativeUserLogged ? operativeUsersCanSeeFarmersInfo === 1 : true) && <El.PageHeaderActionButton
        onClick={() => navigate(RouteTypes.DAIRY_FACTORY_PRICE_ACTIVITY_LOGS_HISTORY)}
      >{t('History')}</El.PageHeaderActionButton>
      }
    </>
  }

  function renderFilters() {
    return <FiltersWrapper>
      <Input
        icon={{ name: 'search', circular: true, link: true }}
        placeholder={t('Search...')}
        value={search}
        onChange={(e, data) => setSearch(data.value)}
      />
      <Dropdown
        placeholder={t('Select activity action')}
        noResultsMessage={t('No results found....')}
        options={activityLogOptions?.map(activityLog => ({ value: activityLog.key, text: activityLog.label })) || []}
        onChange={(e, data) => setDataFiltered({
          ...dataFiltered,
          action: data.value as string
        })}
        clearable
        selection
        search
        selectOnBlur={false}
      />
      <DateInput
        name='date'
        autoComplete='off'
        placeholder={t('Date from')}
        closable
        clearIcon={'close'}
        onKeyPress={handleKeyPress}
        clearable
        localization={languageLocal.code}
        closeOnMouseLeave={false}
        dateFormat={'DD.MM.YYYY'}
        className={'datePicker'}
        value={dataFiltered.dateFrom}
        iconPosition='left'
        onChange={(event, { name, value }) => setDataFiltered({
          ...dataFiltered,
          dateFrom: value
        })}
      />
      <DateInput
        name='date'
        autoComplete='off'
        closable
        preserveViewMode={true}
        duration={200}
        placeholder={t('Date to')}
        clearIcon={'close'}
        clearable
        localization={languageLocal.code}
        onKeyPress={handleKeyPress}
        closeOnMouseLeave={false}
        dateFormat={'DD.MM.YYYY'}
        className={'datePicker'}
        minDate={dataFiltered.dateFrom}
        value={dataFiltered.dateTo}
        iconPosition='left'
        onChange={(event, { name, value }) => {
          setDataFiltered({
            ...dataFiltered,
            dateTo: value
          })
        }}
      />
    </FiltersWrapper>
  }

  function renderContent() {
    return <TableComponent
      animated
      isFetchingData={isFetching}
      tableColumnHeaders={getTableColumnHeaders()}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data) => setDataFiltered(data)}
      total={filtered}
      rows={rows}
    />
  }

  const { filtered, rows } = activityLogData ? activityLogData : { filtered: 0, rows: [] }

  return <Layout
    segmented
    actionAlignToTop
    actionNoWrap
    isBusy={isBusyData || isBusyActivityLogOptions}
    actions={!lightUserLogged && renderActions()}
    filters={renderFilters()}
    title={t('Activity Logs')}>
    {activityLogData && activityLogOptions && renderContent()}
  </Layout>;
};

export default ActivityLogs;
